import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import moment, { Duration } from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable } from 'rxjs';
import { EscalationComponent } from '../../components/escalation/escalation.component';
import { EscalationModel } from '../../components/escalation/escalation.model';
import { ConfigConstants } from '../../config/config-constants';
import { NOTIFICATION_STATUS_CODE } from '../../enums/notification-status-code.enum';
import { NotificationTimerModel } from '../../models/low-level/notification-timer.model';
import { NotificationModel } from '../../models/low-level/notification.model';
import { NotificationService } from '../notification/notification.service';
import { OdataEscalationService } from './odata-escalation.service';

@Injectable({ providedIn: 'root' })
export class EscalationService {
    private escalationTimers: NotificationTimerModel[] = [];
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly notificationService: NotificationService,
        private readonly modalService: BsModalService,
        private readonly odataEscalationService: OdataEscalationService,
    ) {}

    /**
     * This function adds the timer based on the duration (i.e from OSR start time till now)
     * If the duration is more than say 4 hrs the event has to fire immediately
     * This functionality is only valid for MS type notifications.
     *
     * @param notification
     * @param durationPassed
     */
    public setTimerForEscalation(notification: NotificationModel, durationPassed: Duration): void {
        if (notification.notificationType !== ConfigConstants.NotificationTypes.Service) {
            return;
        }
        const timer =
            durationPassed.asHours() >= ConfigConstants.escalationTimeoutAlertTimingInHours
                ? 0
                : ConfigConstants.escalationTimeoutAlertTimingInHours - durationPassed.asHours(); // calc ulating the time difference

        const notificationTimer = new NotificationTimerModel(
            notification.identifier,
            window.setTimeout(
                () => this.showEscalationTimeoutModal(notification),
                timer *
                    ConfigConstants.timeMultiplier *
                    ConfigConstants.timeMultiplier *
                    ConfigConstants.milliSecToSecConversionFactor,
            ),
        );

        this.escalationTimers.push(notificationTimer);
    }

    /**
     * This function removes the already existing timer
     *
     * @param notification
     */
    public removeEscalationTimer(notification: NotificationModel): void {
        this.escalationTimers.forEach((notificationTimerModel) => {
            if (notificationTimerModel.notificationId.equalsTo(notification.identifier)) {
                // TODO : Use cancellation handler to cancel timer
                window.clearTimeout(notificationTimerModel.timer);
            }
        });
    }

    /**
     * This function checks if any notification of type Service (MS) is in Progress status
     * If yes, then it calculate the difference between the OSR start time and current time
     * and set the timer to raise the escalation alert
     */
    public checkEscalationRequired(): void {
        const notifications = this.notificationService.getAllNotificationsInProgress();

        notifications.forEach((notification) => {
            if (notification && notification.notificationType === ConfigConstants.NotificationTypes.Service) {
                // noti fication in progress might have the status code OSR or TRAV
                const status = this.notificationService.getStatus(notification);
                // chec ks if notification status is OSR
                const osrStatus = status.statusCode === NOTIFICATION_STATUS_CODE.OSR ? status : undefined;
                if (!osrStatus) {
                    return;
                }

                const isEscalated = this.notificationService.getEscalated(notification);
                if (isEscalated) {
                    return;
                }

                const timerExists = this.checkTimerAddedOnNotification(notification);
                if (timerExists) {
                    return;
                }

                const now = moment();
                const osrDuration = moment.duration(now.diff(osrStatus.startTime));
                this.setTimerForEscalation(notification, osrDuration);
            }
        });
    }

    public saveEscalationStatus(escalation: EscalationModel): Observable<unknown> {
        return this.odataEscalationService.saveEscalationStatus(escalation);
    }

    public showEscalationPopup(notification: NotificationModel): void {
        this.modalService.show(EscalationComponent, {
            backdrop: 'static',
            class: 'modal-dialog modal-fullscreen-lg-down',
            initialState: {
                notification,
            },
            ariaLabelledBy: 'escalation-modal-title',
        });
    }

    /**
     * This function checks if timer is already added for this notification.
     *
     * @param notification
     */
    private checkTimerAddedOnNotification(notification: NotificationModel): boolean {
        const notificationTimer = this.escalationTimers.filter((timer) =>
            timer.notificationId.equalsTo(notification.identifier),
        );
        return notificationTimer.length > 0;
    }

    /**
     * This function will open notification Escalation Timeout modal
     *
     * @param notification
     */
    private showEscalationTimeoutModal(notification: NotificationModel): void {
        const status = this.notificationService.getStatus(notification);

        if (status.statusCode !== NOTIFICATION_STATUS_CODE.OSR) {
            this.log.error(
                `EscalationService: showEscalationTimeoutModal: Tried to escalate notification ${notification.identifier.toString()} without OSR status!`,
            );
            return;
        }

        this.showEscalationPopup(notification);
    }
}
